import React, { useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { PiPackage, PiPlus, PiUploadSimple } from 'react-icons/pi';
import { ProductFilters } from '../molecules/product-filters';
import type { ProductFiltersState } from '../../types/product-filters.types';
import { useProducts } from 'src/modules/shared/hooks/api/catalog/products';
import {
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
} from 'src/modules/shared/hooks/api/catalog/products.mutation';
import { ProductFormModal } from '../molecules/product-form-modal';
import type { ProductFormValues } from '../../utils/product-validations';
import { ProductImportModal } from '../molecules/product-import-modal';
import { ProductsTable } from '../organisms/products-table';
import { useAlert } from 'src/contexts/alert-context';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import {
  type CatalogProduct,
  CatalogProductCreateBodyParams,
  CatalogProductUpdateBodyParams,
  CustomFieldEntityType,
} from 'src/api/generated';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';
import { TokenPagination } from 'src/modules/shared/components/molecules/paginations/token-pagination';
import {
  type ProductsSearch,
  productsSearchSchema,
} from '../../schemas/products-search.schema';
import { Route as CatalogProductsRoute } from 'src/routes/catalog/products';
import { useCustomFieldDefinitions } from 'src/modules/shared/hooks/api/custom-fields/definitions';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

const createCurrencyByFormValue: Record<
  ProductFormValues['currency'],
  CatalogProductCreateBodyParams.currency
> = {
  CLP: CatalogProductCreateBodyParams.currency.CLP,
  UF: CatalogProductCreateBodyParams.currency.UF,
};

const updateCurrencyByFormValue: Record<
  ProductFormValues['currency'],
  CatalogProductUpdateBodyParams.currency
> = {
  CLP: CatalogProductUpdateBodyParams.currency.CLP,
  UF: CatalogProductUpdateBodyParams.currency.UF,
};

export const ProductsPage: React.FC = () => {
  const navigate = useNavigate({ from: CatalogProductsRoute.id });
  const {
    search: searchTerm = '',
    currency = 'all',
    pageSize = 10,
  } = useSearch({
    from: CatalogProductsRoute.id,
    select: (search: ProductsSearch) => ({
      search: search.search,
      currency: search.currency,
      pageSize: search.pageSize,
    }),
  });

  const { showAlert } = useAlert();
  const { t } = useI18n();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [productToEdit, setProductToEdit] = useState<CatalogProduct | null>(
    null
  );
  const [productToDelete, setProductToDelete] = useState<CatalogProduct | null>(
    null
  );

  const filters = useMemo<ProductFiltersState>(() => {
    return {
      search: searchTerm,
      currency: currency as ProductFiltersState['currency'],
    };
  }, [searchTerm, currency]);

  const queryParams = useMemo(() => {
    const builder = createQuery().orderByDesc('createdAt').take(pageSize);
    if (filters.search && filters.search.trim().length > 0) {
      builder.search(filters.search.trim());
    }
    if (filters.currency !== 'all') {
      builder.equals('currency', filters.currency);
    }
    return builder.build();
  }, [filters, pageSize]);

  const {
    products,
    loading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProducts(queryParams);

  const {
    definitions: productCustomFieldDefinitions,
    loading: loadingProductCustomFields,
  } = useCustomFieldDefinitions({
    entityType: CustomFieldEntityType.PRODUCT,
    pageSize: 200,
  });

  const { createProduct, loading: creatingProduct } = useCreateProduct({
    onSuccess: () => {
      showAlert(t('catalog.products.alert.create.success'), 'success');
    },
    onError: () => {
      showAlert(t('catalog.products.alert.create.error'), 'error');
    },
  });
  const { updateProduct, loading: updatingProduct } = useUpdateProduct({
    onSuccess: () => {
      showAlert(t('catalog.products.alert.update.success'), 'success');
    },
    onError: () => {
      showAlert(t('catalog.products.alert.update.error'), 'error');
    },
  });
  const { deleteProduct, loading: deletingProduct } = useDeleteProduct({
    onSuccess: () => {
      showAlert(t('catalog.products.alert.delete.success'), 'success');
    },
    onError: () => {
      showAlert(t('catalog.products.alert.delete.error'), 'error');
    },
  });

  const handleNavigateSearch = (
    updater: (prev: ProductsSearch) => ProductsSearch
  ) => {
    void navigate({
      to: CatalogProductsRoute.to,
      search: (prev) => updater(productsSearchSchema.parse(prev)),
    });
  };

  const handleFiltersChange = (nextFilters: ProductFiltersState) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      search: nextFilters.search?.trim() ? nextFilters.search : undefined,
      currency: nextFilters.currency,
    }));
  };

  const handleClearFilters = () => {
    handleNavigateSearch((prev) => ({
      ...prev,
      search: undefined,
      currency: 'all',
    }));
  };

  const handlePageSizeChange = (newSize: number) => {
    handleNavigateSearch((prev) => ({
      ...prev,
      pageSize: newSize,
    }));
  };

  const handleOpenCreate = () => {
    setProductToEdit(null);
    setIsFormOpen(true);
  };

  const handleEditProduct = (product: CatalogProduct) => {
    setProductToEdit(product);
    setIsFormOpen(true);
  };

  const handleDeleteProduct = (product: CatalogProduct) => {
    setProductToDelete(product);
  };

  const handleSubmitForm = async (
    values: ProductFormValues,
    customFields: Record<string, unknown>
  ) => {
    const customFieldsPayload =
      Object.keys(customFields).length > 0 ? customFields : undefined;

    const createPayload: CatalogProductCreateBodyParams = {
      ...values,
      currency: createCurrencyByFormValue[values.currency],
      customFields: customFieldsPayload,
    };

    if (productToEdit) {
      const updatePayload: CatalogProductUpdateBodyParams = {
        ...values,
        currency: updateCurrencyByFormValue[values.currency],
        customFields: customFieldsPayload,
      };
      await updateProduct({ id: productToEdit.id, ...updatePayload });
    } else {
      await createProduct(createPayload);
    }
    setIsFormOpen(false);
    setProductToEdit(null);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;

    await deleteProduct(productToDelete.id);
    setProductToDelete(null);
  };

  const isMutating = creatingProduct || updatingProduct;

  const isLoading = loading || loadingProductCustomFields;

  const renderContent = () => {
    if (error) {
      return (
        <div className="flex flex-1 items-center justify-center p-6">
          <ErrorState
            title={t('catalog.products.error.title')}
            description={t('catalog.products.error.description')}
            onAction={() => refetch()}
            actionContent={t('catalog.products.error.retry')}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col flex-1 overflow-hidden">
        <div className="flex flex-col gap-4 p-4 pb-8 overflow-y-auto flex-1">
          <TokenPagination
            pageSize={pageSize}
            currentCount={products.length}
            onPageSizeChange={handlePageSizeChange}
            onNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isLoading={isFetchingNextPage}
          />
          <ProductsTable
            products={products}
            loading={isLoading}
            isLoadingMore={isFetchingNextPage}
            onEdit={handleEditProduct}
            onDelete={handleDeleteProduct}
            customFieldDefinitions={productCustomFieldDefinitions}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <Panel className="flex flex-col h-full">
        <PanelHeader
          className="px-4 py-3 border-b"
          actions={
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button
                variant="primary"
                appearance="soft"
                size="md"
                onClick={() => setIsImportOpen(true)}
                className="flex items-center justify-center gap-2"
              >
                <PiUploadSimple className="w-4 h-4" />
                <span className="whitespace-nowrap">
                  {t('catalog.products.import')}
                </span>
              </Button>
              <Button
                variant="primary"
                appearance="filled"
                size="md"
                onClick={handleOpenCreate}
                className="flex items-center justify-center gap-2"
              >
                <PiPlus className="w-4 h-4 flex-shrink-0" />
                <span className="whitespace-nowrap">
                  {t('catalog.products.new')}
                </span>
              </Button>
            </div>
          }
        >
          <div className="flex items-center gap-4">
            <PiPackage className="w-7 h-7 flex-shrink-0" />
            <Text as="h2" size="h2" weight="medium">
              {t('catalog.products.title')}
            </Text>
          </div>
        </PanelHeader>

        <ProductFilters
          filters={filters}
          onChange={handleFiltersChange}
          onClear={handleClearFilters}
        />

        {renderContent()}
      </Panel>

      <ProductFormModal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setProductToEdit(null);
        }}
        onSubmit={handleSubmitForm}
        loading={isMutating}
        product={productToEdit}
        customFieldDefinitions={productCustomFieldDefinitions}
      />

      <ProductImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
      />

      <ConfirmModal
        isOpen={Boolean(productToDelete)}
        onClose={() => setProductToDelete(null)}
        onConfirm={handleConfirmDelete}
        confirmText={t('catalog.products.confirmDelete.confirm')}
        cancelText={t('catalog.products.confirmDelete.cancel')}
        loading={deletingProduct}
        variant="danger"
        title={t('catalog.products.confirmDelete.title')}
      >
        <Text>
          {t('catalog.products.confirmDelete.messagePrefix')}{' '}
          <strong>
            {productToDelete?.name ??
              t('catalog.products.confirmDelete.fallbackName')}
          </strong>
          {t('catalog.products.confirmDelete.messageSuffix')}
        </Text>
      </ConfirmModal>
    </div>
  );
};
